import { createCRUDHook } from './createCRUDHook';
import {
  CustomerOrderReportDto,
  DailyPartWidgetDto,
  MakeupPartDetailDto,
  MakeupReportDto,
  MangersActivityReportDto,
  OpenOrderDto,
  OpenOrderWithPartDto,
  OrderItemDto,
  ScheduledItemWithoutStationDto,
  ScheduledPartWidgetDto,
  StationCountDto,
  UserActivityDto,
  UtilizationReportDto,
  UtilizationReportWorkFlowDto,
  NLReportRequestDto,
  NLReportResponseDto,
} from 'utils/models';
import {
  GET_COMPLETED_SHIPMENT_BY_CARRIERS,
  GET_CUSTOMER_OPEN_ORDERS,
  GET_CUSTOMER_OPEN_ORDERS_BY_PRODUCTION_PROCESS_ID,
  GET_CUSTOMER_ORDER_REPORT,
  GET_DAILY_PART_WIDGET_DATA_BY_STATION,
  GET_MAKEUP_PART_DETAIL_REPORT_DATA_BY_PART_ID,
  GET_MAKEUP_PART_REPORT_DATA,
  GET_MANAGER_ACTIVITY_BY_STATION_ID,
  GET_PRODUCTION_ORDERS_BY_PRODUCTION_PROCESS_ID,
  GET_SCHEDULED_PART_WIDGET_DATA_BY_STATION,
  GET_USER_ACTIVITY_WITH_STATION_DETAIL,
  GET_USER_ACTIVITY_WITH_STATION_DETAIL_BY_DATE,
  GET_USER_PERSONAL_BEST_AND_TODAY_COUNT,
  GET_UTILIZATION_REPORT_BY_WORKFLOW_ID,
  REPORT,
  SEND_TRACKING_REPORT,
  TBL_CUSTOMER_ORDER_SHIPMENT,
  GET_AVERAGE_DAYS_TO_SHIP_REPORT_DATA,
  GET_AVERAGE_DAY_BY_DAY_TO_SHIP_REPORT_DATA,
  GET_PART_OPEN_ORDER_REPORT,
  TLKP_CUSTOMER_ORDER_ITEM,
  GET_PRODUCTION_NOT_COMPLETE_ORDER_ITEM,
  GET_SCHEDULED_ITEMS_WITHOUT_STATION,
  NL_REPORT,
  NL_REPORT_GENERATE,
} from './apiEndPoints';

interface ReportCustomOperations {
  GetUserActivityWithStationDetail: (
    selectedView: number,
    staffId: number
  ) => Promise<UserActivityDto[]>;
  GetCustomerOpenOrders: () => Promise<OpenOrderDto[]>;
  GetManagerActivityWithStationDetail: (
    startDate: Date,
    endDate: Date,
    stationId: number
  ) => Promise<MangersActivityReportDto[]>;
  GetFabricationReport: (
    processIds: string,
    IsWarehouseOrder: boolean
  ) => Promise<OpenOrderDto[]>;
  GetProductionOrderReportNotCompleted: (
    processIds: string
  ) => Promise<OpenOrderDto[]>;
  GetMakeupPartReportData: (
    fromDate: string,
    toDate: string
  ) => Promise<MakeupReportDto[]>;
  GetMakeupPartReportDataByPartId: (
    fromDate: string,
    toDate: string,
    partId: number
  ) => Promise<MakeupPartDetailDto[]>;
  GetUserActivityWithStationDetailByDate: (
    fromDate: string,
    staffId: number,
    station: string
  ) => Promise<UserActivityDto[]>;
  GetUserPersonalBestAndTodayCount: (
    staffId: number,
    station: string
  ) => Promise<StationCountDto>;
  GetDailyPartWidgetDataByStation: (
    startDate: string,
    endDate: string,
    stationId: number
  ) => Promise<DailyPartWidgetDto[]>;
  GetScheduledPartWidgetDataByStation: (
    startDate: string,
    endDate: string,
    stationId: number
  ) => Promise<ScheduledPartWidgetDto[]>;
  fetchCustomerOrderShipmentData: (
    customerIds: string
  ) => Promise<CustomerOrderReportDto[]>;
  fetchUtilizationReport: (
    prevDays: number,
    nextDays: number,
    stationId: number
  ) => Promise<UtilizationReportDto>;
  fetchUtilizationReportByWorkFlowId: (
    processId: number
  ) => Promise<UtilizationReportWorkFlowDto[]>;
  fetchTrackingReportByCarrier: (
    fromDate: string,
    toDate: string,
    carrier: string
  ) => Promise<any>;
  sendTrackingReport: (email: string, param: any) => Promise<any>;
  FetchAverageDaysToShipReportData: (
    customerId?: number,
    startDate?: string,
    endDate?: string
  ) => Promise<any>;
  FetchAverageDayByDayToShipReportData: (
    customerId?: number,
    startDate?: string,
    endDate?: string
  ) => Promise<any>;
  fetchOpenOrderReportByPartId: (
    partId: number
  ) => Promise<OpenOrderWithPartDto>;
  getProductionNotCompleteOrderItem: (
    orderId: number
  ) => Promise<OrderItemDto[]>;
  getScheduledItemsWithoutStation: () => Promise<
    ScheduledItemWithoutStationDto[]
  >;
  generateNLReport: (
    request: NLReportRequestDto
  ) => Promise<NLReportResponseDto>;
}

const { Context, useManager, useConsumer } = createCRUDHook<
  any,
  any,
  ReportCustomOperations
>({
  entityName: 'Report',
  endpoint: REPORT,

  customOperations: (auth) => ({
    GetDailyPartWidgetDataByStation: async (
      startDate: string,
      endDate: string,
      stationId: number
    ): Promise<DailyPartWidgetDto[]> => {
      try {
        const res = await auth.makeAuthenticatedGet(
          `${REPORT}${GET_DAILY_PART_WIDGET_DATA_BY_STATION}?startDate=${startDate}&endDate=${endDate}&stationId=${stationId}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    GetScheduledPartWidgetDataByStation: async (
      startDate: string,
      endDate: string,
      stationId: number
    ): Promise<ScheduledPartWidgetDto[]> => {
      try {
        const res = await auth.makeAuthenticatedGet(
          `${REPORT}${GET_SCHEDULED_PART_WIDGET_DATA_BY_STATION}?startDate=${startDate}&endDate=${endDate}&stationId=${stationId}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    GetUserActivityWithStationDetail: async (
      selectedView: number,
      staffId: number
    ): Promise<UserActivityDto[]> => {
      try {
        const res = await auth.makeAuthenticatedGet(
          `${REPORT}${GET_USER_ACTIVITY_WITH_STATION_DETAIL}?selectedView=${selectedView}&staffId=${staffId}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    GetManagerActivityWithStationDetail: async (
      startDate: Date,
      endDate: Date,
      stationId: number
    ): Promise<MangersActivityReportDto[]> => {
      try {
        const res = await auth.makeAuthenticatedGet(
          `${REPORT}${GET_MANAGER_ACTIVITY_BY_STATION_ID}?startDate=${startDate}&endDate=${endDate}&stationId=${stationId}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    GetUserActivityWithStationDetailByDate: async (
      fromDate: string,
      staffId: number,
      station: string
    ): Promise<UserActivityDto[]> => {
      try {
        const res = await auth.makeAuthenticatedGet(
          `${REPORT}${GET_USER_ACTIVITY_WITH_STATION_DETAIL_BY_DATE}?fromDate=${fromDate}&staffId=${staffId}&station=${station}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    GetUserPersonalBestAndTodayCount: async (
      staffId: number,
      station: string
    ): Promise<StationCountDto> => {
      try {
        const res = await auth.makeAuthenticatedGet(
          `${REPORT}${GET_USER_PERSONAL_BEST_AND_TODAY_COUNT}?staffId=${staffId}&station=${station}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    GetCustomerOpenOrders: async (): Promise<OpenOrderDto[]> => {
      try {
        const res = await auth.makeAuthenticatedGet(
          `${REPORT}${GET_CUSTOMER_OPEN_ORDERS}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    GetFabricationReport: async (
      processIds: string,
      IsWarehouseOrder: boolean
    ): Promise<OpenOrderDto[]> => {
      try {
        const res = await auth.makeAuthenticatedGet(
          `${REPORT}${GET_CUSTOMER_OPEN_ORDERS_BY_PRODUCTION_PROCESS_ID}?processIds=${processIds}&IsWarehouseOrder=${IsWarehouseOrder}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    GetProductionOrderReportNotCompleted: async (
      processIds: string
    ): Promise<OpenOrderDto[]> => {
      try {
        const res = await auth.makeAuthenticatedGet(
          `${REPORT}${GET_PRODUCTION_ORDERS_BY_PRODUCTION_PROCESS_ID}?processIds=${processIds}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    GetMakeupPartReportData: async (
      fromDate: string,
      toDate: string
    ): Promise<MakeupReportDto[]> => {
      try {
        const res = await auth.makeAuthenticatedGet(
          `${REPORT}${GET_MAKEUP_PART_REPORT_DATA}?fromDate=${fromDate}&toDate=${toDate}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    GetMakeupPartReportDataByPartId: async (
      fromDate: string,
      toDate: string,
      partId: number
    ): Promise<MakeupPartDetailDto[]> => {
      try {
        const res = await auth.makeAuthenticatedGet(
          `${REPORT}${GET_MAKEUP_PART_DETAIL_REPORT_DATA_BY_PART_ID}?fromDate=${fromDate}&toDate=${toDate}&partId=${partId}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    fetchCustomerOrderShipmentData: async (
      customerIds: string
    ): Promise<CustomerOrderReportDto[]> => {
      try {
        const res = await auth.makeAuthenticatedGet(
          REPORT + GET_CUSTOMER_ORDER_REPORT + `?customerIds=${customerIds}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    fetchUtilizationReport: async (
      prevDays: number,
      nextDays: number,
      stationId: number
    ): Promise<UtilizationReportDto> => {
      try {
        const res = await auth.makeAuthenticatedGet(
          REPORT +
            '/TestUtilizationReport' +
            `?prevDays=${prevDays}&nextDays=${nextDays}&stationId=${stationId}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    fetchUtilizationReportByWorkFlowId: async (
      processId: number
    ): Promise<UtilizationReportWorkFlowDto[]> => {
      try {
        const res = await auth.makeAuthenticatedGet(
          REPORT +
            GET_UTILIZATION_REPORT_BY_WORKFLOW_ID +
            `?workflowId=${processId}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    fetchTrackingReportByCarrier: async (
      fromDate: string,
      toDate: string,
      carrier: string
    ): Promise<any> => {
      try {
        const res = await auth.makeAuthenticatedGet(
          `${TBL_CUSTOMER_ORDER_SHIPMENT}${GET_COMPLETED_SHIPMENT_BY_CARRIERS}?fromDate=${fromDate}&toDate=${toDate}&carrier=${carrier}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    sendTrackingReport: async (email: string, param: any): Promise<any> => {
      const formData = new FormData();
      formData.append('file', param);
      try {
        const res = await auth.makeAuthenticatedPost(
          REPORT + SEND_TRACKING_REPORT + `?email=${email}`,
          formData
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return null;
      }
    },
    FetchAverageDaysToShipReportData: async (
      customerId?: number,
      startDate?: string,
      endDate?: string
    ): Promise<any> => {
      try {
        const res = await auth.makeAuthenticatedGet(
          `${REPORT}${GET_AVERAGE_DAYS_TO_SHIP_REPORT_DATA}?customerId=${customerId}&startDate=${startDate}&endDate=${endDate}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    FetchAverageDayByDayToShipReportData: async (
      customerId?: number,
      startDate?: string,
      endDate?: string
    ): Promise<any> => {
      try {
        const res = await auth.makeAuthenticatedGet(
          `${REPORT}${GET_AVERAGE_DAY_BY_DAY_TO_SHIP_REPORT_DATA}?customerId=${customerId}&startDate=${startDate}&endDate=${endDate}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    fetchOpenOrderReportByPartId: async (
      partId: number
    ): Promise<OpenOrderWithPartDto> => {
      try {
        const res = await auth.makeAuthenticatedGet(
          REPORT + GET_PART_OPEN_ORDER_REPORT + `?partId=${partId}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    getProductionNotCompleteOrderItem: async (
      orderId: number
    ): Promise<OrderItemDto[]> => {
      try {
        const res = await auth.makeAuthenticatedGet(
          TLKP_CUSTOMER_ORDER_ITEM +
            GET_PRODUCTION_NOT_COMPLETE_ORDER_ITEM +
            `?orderId=${orderId}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    getScheduledItemsWithoutStation: async (): Promise<
      ScheduledItemWithoutStationDto[]
    > => {
      try {
        const res = await auth.makeAuthenticatedGet(
          TLKP_CUSTOMER_ORDER_ITEM + GET_SCHEDULED_ITEMS_WITHOUT_STATION
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    generateNLReport: async (
      request: NLReportRequestDto
    ): Promise<NLReportResponseDto> => {
      try {
        const res = await auth.makeAuthenticatedPost(
          NL_REPORT + NL_REPORT_GENERATE,
          {
            query: request.query.trim(),
            maxRows: request.maxRows ?? 1000,
            timeoutSeconds: request.timeoutSeconds ?? 30,
          }
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },
  }),
});

export const ReportContext = Context;
export const useReportManager = useManager;
export const useReport = useConsumer;
